import { isReadOnly } from '@/lib/permissions'
import { type MedicationRequest, useResetIdVerificationMutation } from '@/services/med-req-detail'
import { borderRadius, colors } from '@/theme'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import { useState } from 'react'

const REASON_MAX_LENGTH = 500

interface ResetIdVerificationDialogProps {
  open: boolean
  medRequest: MedicationRequest
  onClose: () => void
}

export default function ResetIdVerificationDialog({ open, medRequest, onClose }: ResetIdVerificationDialogProps) {
  const resetMutation = useResetIdVerificationMutation()
  const [reason, setReason] = useState('')
  const isLoading = resetMutation.isPending
  const isReasonTooLong = reason.length > REASON_MAX_LENGTH

  const patientName = [medRequest.patient.firstName, medRequest.patient.lastName].filter(Boolean).join(' ')

  const handleClose = () => {
    if (isLoading) return
    setReason('')
    onClose()
  }

  const handleConfirm = () => {
    if (isReadOnly()) return

    resetMutation.mutate(
      { orderItemHash: medRequest.id, reason },
      {
        onSuccess: () => {
          setReason('')
          onClose()
        }
      }
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { borderRadius: borderRadius.md } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant='sectionTitle'>Re-verify patient ID</Typography>
          <IconButton onClick={handleClose} size='small' disabled={isLoading}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ fontSize: 14, color: colors.text.primary, mb: 1.5 }}>
          Send order <strong>{medRequest.orderNumber}</strong> back to System ID Check for{' '}
          <strong>{patientName || 'this patient'}</strong>?
        </Typography>
        <Typography sx={{ fontSize: 14, color: colors.text.secondary, mb: 3 }}>
          ID verified will be cleared on the patient record and the automated ID check will run again. If it passes, the
          order carries on through the normal flow without a prescriber. If it cannot be verified, the patient is asked
          to verify themselves as usual. This action and your name are recorded on the timeline.
        </Typography>

        <Typography variant='fieldLabel' sx={{ display: 'block', mb: 1 }}>
          Reason (optional)
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          placeholder='e.g. Age unclear after patient merge'
          value={reason}
          onChange={event => setReason(event.target.value)}
          disabled={isLoading}
          error={isReasonTooLong}
          helperText={
            isReasonTooLong
              ? `Must be ${REASON_MAX_LENGTH} characters or fewer`
              : `${reason.length}/${REASON_MAX_LENGTH}`
          }
        />

        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
            <CircularProgress size={16} />
            <Typography sx={{ fontSize: 13, color: colors.text.secondary }}>
              Restarting the ID check — please keep this window open.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button variant='ghost' onClick={handleClose} disabled={isLoading}>
          Keep current status
        </Button>
        <Button variant='primary' onClick={handleConfirm} disabled={isLoading || isReasonTooLong}>
          {isLoading ? 'Sending back...' : 'Send back to ID check'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
